/**
 * Speicherung eines Patterns im Browser (localStorage).
 *
 * Diese Schicht kennt das fachliche Modell und die Serialisierung,
 * aber keine DOM- oder UI-Belange. Fehlerhafte oder fremde Daten
 * werden robust behandelt: Statt zu werfen, liefert das Laden in
 * solchen Faellen `null`, damit die Anwendung mit einem leeren
 * Pattern fortfahren kann.
 */

import { DrumPattern } from '../model/DrumPattern';

export const STORAGE_KEY = 'drumpattern-editor:pattern:v1';

/**
 * Abstraktion ueber den konkreten Speicher. In der Anwendung ist dies
 * `window.localStorage`; in Tests kann eine Attrappe eingesetzt werden.
 */
export interface PatternStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function resolveStore(store?: PatternStore): PatternStore | null {
  if (store) {
    return store;
  }
  try {
    if (typeof localStorage !== 'undefined') {
      return localStorage;
    }
  } catch {
    // Zugriff auf localStorage kann in manchen Umgebungen scheitern.
  }
  return null;
}

/**
 * Speichert ein Pattern. Fehler (z. B. voller Speicher) werden
 * abgefangen und als `false` gemeldet.
 */
export function savePattern(
  pattern: DrumPattern,
  store?: PatternStore,
): boolean {
  const target = resolveStore(store);
  if (!target) {
    return false;
  }
  try {
    target.setItem(STORAGE_KEY, JSON.stringify(pattern.toSerializable()));
    return true;
  } catch {
    return false;
  }
}

/**
 * Laedt ein Pattern. Liefert `null`, wenn nichts gespeichert ist oder
 * die gespeicherten Daten ungueltig bzw. beschaedigt sind.
 */
export function loadPattern(store?: PatternStore): DrumPattern | null {
  const target = resolveStore(store);
  if (!target) {
    return null;
  }

  let raw: string | null;
  try {
    raw = target.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) {
    return null;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  try {
    return DrumPattern.fromSerializable(parsed);
  } catch {
    // Ungueltige gespeicherte Daten werden verworfen.
    return null;
  }
}

/** Entfernt das gespeicherte Pattern. */
export function clearStoredPattern(store?: PatternStore): void {
  const target = resolveStore(store);
  if (!target) {
    return;
  }
  try {
    target.removeItem(STORAGE_KEY);
  } catch {
    // Fehler beim Loeschen werden ignoriert.
  }
}
